import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, getDocs, where } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { User } from '../types';

interface AuthContextType {
  currentUser: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  isAdmin: boolean;
  adminUserId: string | null;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUserId, setAdminUserId] = useState<string | null>(null);

  // Function to detect admin users and check if current user is an admin
  // Returns { isCurrentUserAdmin, primaryAdminId } where primaryAdminId is used for calendar defaulting
  const detectAdminStatus = async (currentUserId: string): Promise<{ isCurrentUserAdmin: boolean; primaryAdminId: string | null }> => {
    try {
      console.log('🔍 Detecting admin status...');
      console.log('🔍 Current user:', currentUserId);
      
      const usersRef = collection(db, 'users');
      
      // Query for all users with admin: true
      try {
        console.log('🔍 Querying for admin users...');
        const adminQuery = query(usersRef, where('admin', '==', true));
        const usersSnap = await getDocs(adminQuery);
        console.log('🔍 Admin query SUCCESS: found', usersSnap.docs.length, 'admin users');
        
        if (!usersSnap.empty) {
          // Get all admin user IDs
          const adminIds = usersSnap.docs.map(doc => doc.id);
          const adminEmails = usersSnap.docs.map(doc => doc.data().email);
          console.log('✅ Admin users:', adminEmails);
          
          // Check if current user is among the admins
          const isCurrentUserAdmin = adminIds.includes(currentUserId);
          
          // Use first admin as primary (for calendar defaulting for non-admins)
          const primaryAdminId = usersSnap.docs[0].id;
          
          console.log(`🔐 Current user ${isCurrentUserAdmin ? 'IS' : 'is NOT'} an admin`);
          console.log(`🔐 Primary admin ID: ${primaryAdminId}`);
          
          return { isCurrentUserAdmin, primaryAdminId };
        }
        
      } catch (queryError) {
        console.error('❌ Admin query failed:', queryError);
        return { isCurrentUserAdmin: false, primaryAdminId: null };
      }
      
      console.log('ℹ️ No admin users found');
      return { isCurrentUserAdmin: false, primaryAdminId: null };
    } catch (error) {
      console.error('❌ Error detecting admin status:', error);
      return { isCurrentUserAdmin: false, primaryAdminId: null };
    }
  };

  const createUserDocument = async (firebaseUser: FirebaseUser): Promise<User> => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        displayName: firebaseUser.displayName || 'User',
        photoURL: firebaseUser.photoURL || undefined,
      };

      await setDoc(userRef, {
        ...userData,
        createdAt: new Date(),
        lastLogin: new Date(),
      });

      return userData;
    } else {
      // Update user data and last login for existing users
      const userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        displayName: firebaseUser.displayName || 'User',
        photoURL: firebaseUser.photoURL || undefined,
      };

      await setDoc(userRef, {
        ...userData,
        lastLogin: new Date(),
      }, { merge: true });

      return userData;
    }
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Note: Google API scopes are no longer needed here since all Google API calls
    // go through Firebase Functions using a service account with domain-wide delegation.
    // Firebase Auth only needs basic profile access which is included by default.
    
    const result = await signInWithPopup(auth, provider);
    
    // Check if user is from Brevity domain
    const allowedDomain = 'bvt.co.nz';
    if (result.user.email && !result.user.email.endsWith(`@${allowedDomain}`)) {
      await signOut(auth);
      throw new Error(`Access restricted to ${allowedDomain} domain only`);
    }

    console.log('✅ User signed in successfully via Firebase Auth');
  };

  const logout = async () => {
    await signOut(auth);
    console.log('✅ User signed out');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        try {
          const userData = await createUserDocument(firebaseUser);
          setCurrentUser(userData);
          
          // Detect admin status - checks if current user is an admin
          console.log('🔐 User authenticated, now detecting admin status...');
          const { isCurrentUserAdmin, primaryAdminId } = await detectAdminStatus(firebaseUser.uid);
          setIsAdmin(isCurrentUserAdmin);
          setAdminUserId(primaryAdminId);
          
          console.log(`🔐 User ${firebaseUser.uid} is ${isCurrentUserAdmin ? 'ADMIN' : 'NON-ADMIN'}`);
          
        } catch (error) {
          console.error('Error creating user document:', error);
          setCurrentUser(null);
          setIsAdmin(false);
          setAdminUserId(null);
        }
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setAdminUserId(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    firebaseUser,
    loading,
    isAdmin,
    adminUserId,
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
